/**
 * Theme Contract — Surface Layer (/01)
 *
 * Defines the agnostic shape of every theme payload that flows through
 * the SPA. The CarbonSurfaceThemeProvider is the single consumer.
 *
 * The contract is enforced by `validate-theme-payload.ts`. Any payload
 * arriving from the IDL bridge, localStorage, or a Settings tearsheet
 * is validated before injection. Invalid payloads fall back to g100.
 *
 * Aligned with: /00/DataGate.schema.json (RemoteUsersDatabase tenant_config / user_preferences)
 */

/** Carbon base themes. */
export type CarbonThemeName = 'g100' | 'g90' | 'g10' | 'white';

/** Density preset that maps to Carbon spacing scaling. */
export type SurfaceDensity = 'compact' | 'comfortable' | 'spacious';

/**
 * Allowed CSS custom-property overrides.
 *
 * Keys are the literal Carbon token names (without the `--` prefix in
 * code, but emitted with `--` in the injected `<style>` tag).
 *
 * Values are CSS strings — colors as `var(...)` / hex / hsl, fonts as
 * font-family strings, lengths as `px` or `rem`. Validation enforces
 * the type per key.
 */
export interface ThemeTokenOverrides {
  // Backgrounds
  'cds-background'?: string;
  'cds-layer-01'?: string;
  'cds-layer-02'?: string;
  'cds-layer-03'?: string;
  // Interactive
  'cds-interactive'?: string;
  'cds-button-primary'?: string;
  'cds-button-primary-hover'?: string;
  // Border
  'cds-border-subtle'?: string;
  'cds-border-subtle-01'?: string;
  'cds-border-strong'?: string;
  'cds-border-interactive'?: string;
  // Text
  'cds-text-primary'?: string;
  'cds-text-secondary'?: string;
  // Field
  'cds-field-01'?: string;
  // Support / status
  'cds-support-error'?: string;
  'cds-support-success'?: string;
  'cds-support-warning'?: string;
  'cds-support-info'?: string;
  // Link
  'cds-link-primary'?: string;
  // Icon
  'cds-icon-primary'?: string;
  // Focus
  'cds-focus'?: string;
  // Font
  'cds-font-family-sans'?: string;
  'cds-font-family-mono'?: string;
  // Radius — clamp 0 to 24 px (validated, not enforced by TS)
  'cds-button-border-radius'?: string;
  'cds-modal-border-radius'?: string;
  'cds-card-border-radius'?: string;
}

/**
 * Surface-specific overrides that do not map directly to a Carbon
 * token. These live under the `--surface-*` namespace.
 */
export interface SurfaceTokenOverrides {
  'surface-container-max-width'?: string;   // px or rem, clamped 320–2560
  'surface-density'?: SurfaceDensity;
}

/**
 * Payload owned by a tenant (Master / Agency / holding entity).
 * Persisted in RemoteUsersDatabase.tenant_config JSONB column.
 * Supersedes user preferences key-by-key (Matryoshka resolution).
 */
export interface TenantThemeOverride {
  schemaVersion: 1;
  tenantId: string;
  baseTheme?: CarbonThemeName;
  forced: boolean;                          // if true, locks the user out of editing
  tokens?: ThemeTokenOverrides;
  surfaceTokens?: SurfaceTokenOverrides;
  updatedAt: string;                        // ISO 8601
  updatedByIdentityId: string;
}

/**
 * Payload owned by an individual user. Persisted in
 * RemoteUsersDatabase.user_preferences JSONB column.
 */
export interface UserThemePreference {
  schemaVersion: 1;
  identityId: string;
  baseTheme?: CarbonThemeName;
  tokens?: ThemeTokenOverrides;
  surfaceTokens?: SurfaceTokenOverrides;
  updatedAt: string;
}

/**
 * Live in-session edits from a Settings tearsheet. Not yet persisted.
 * Discarded on sign-out.
 */
export interface SessionThemeEdit {
  baseTheme?: CarbonThemeName;
  tokens?: ThemeTokenOverrides;
  surfaceTokens?: SurfaceTokenOverrides;
}

/**
 * The merged result that the provider injects.
 *
 * Resolution order (later wins, key-by-key):
 *   1. Hardcoded fallback (g100, IBM Plex Sans, 0 radius, 1584px max).
 *   2. TenantThemeOverride.
 *   3. UserThemePreference.
 *   4. SessionThemeEdit.
 *
 * `baseTheme` resolves whole-payload (not key-by-key) because the
 * Carbon `<Theme>` wrapper accepts a single string.
 */
export interface MergedThemePayload {
  baseTheme: CarbonThemeName;
  tokens: ThemeTokenOverrides;
  surfaceTokens: SurfaceTokenOverrides;
  /** Layers that contributed, for debug + Swift host visibility. */
  resolvedFromLayers: Array<'fallback' | 'tenant' | 'user' | 'session'>;
}

/** Hardcoded fallback. */
export const SURFACE_THEME_FALLBACK: MergedThemePayload = {
  baseTheme: 'g100',
  tokens: {
    'cds-font-family-sans': "'IBM Plex Sans', system-ui, sans-serif",
    'cds-font-family-mono': "'IBM Plex Mono', ui-monospace, monospace",
    'cds-button-border-radius': '0',
    'cds-modal-border-radius': '0',
    'cds-card-border-radius': '0',
  },
  surfaceTokens: {
    'surface-container-max-width': '1584px',
    'surface-density': 'comfortable',
  },
  resolvedFromLayers: ['fallback'],
};

/** Allowed keys, used by the validator. */
export const ALLOWED_CARBON_TOKEN_KEYS: ReadonlyArray<keyof ThemeTokenOverrides> = [
  'cds-background',
  'cds-layer-01',
  'cds-layer-02',
  'cds-layer-03',
  'cds-interactive',
  'cds-button-primary',
  'cds-button-primary-hover',
  'cds-border-subtle',
  'cds-border-subtle-01',
  'cds-border-strong',
  'cds-border-interactive',
  'cds-text-primary',
  'cds-text-secondary',
  'cds-field-01',
  'cds-support-error',
  'cds-support-success',
  'cds-support-warning',
  'cds-support-info',
  'cds-link-primary',
  'cds-icon-primary',
  'cds-focus',
  'cds-font-family-sans',
  'cds-font-family-mono',
  'cds-button-border-radius',
  'cds-modal-border-radius',
  'cds-card-border-radius',
] as const;

export const ALLOWED_SURFACE_TOKEN_KEYS: ReadonlyArray<keyof SurfaceTokenOverrides> = [
  'surface-container-max-width',
  'surface-density',
] as const;
